import React, { useState, useEffect } from "react";
import { makeStyles, Link as MuiLink, Typography, Box } from "@material-ui/core";
import Link from "next/link";
import classNames from "classnames";

import { formatPrice, getModelCategory, formatPriceRound, isProductNew, buildWarrantyContent } from "./utils";
import ProductImage from "./ProductImage";
import { getProductEligibility } from "components/localOfferEligibility/LocalOfferEligibility";
import CheckCircle from "components/localOffer/assets/yellowCheckCircleDarkMode.svg";
import useCart from "hooks/useCart";
import { OfferTag } from "ui/OfferTag/OfferTag";
import LocalOfferPicto from "components/localOffer/assets/localOfferPicto.svg";
import Tag from "components/design-system/atoms/Tag";
import { ProductStateTag } from "ui/StateTag/StateTag";

function ModelCard(props) {
  const [cart] = useCart();
  const [isEligibleToLocalOffer, setIsEligibleToLocalOffer] = useState(false);

  useEffect(() => {
    setIsEligibleToLocalOffer(getProductEligibility(props));
  }, [cart, props]);

  const classes = useStyle({
    textMargin: props.textMargin,
    marginTop: props.marginTop,
    width: props.width,
    margin: props.margin,
    minWidth: props.minWidth,
    mdWidth: props.mdWidth,
  });

  const MarketPriceTypography = (
    <Typography variant="body2" className={classes.marketPrice}>
      {formatPriceRound(props.market_selling_price)} neuf
    </Typography>
  );

  const DiscountTag = props.discount && (
    <Tag bgcolor="yellow">
      <Box fontWeight={700} color="secondary.main">
        {-props.discount}%
      </Box>
    </Tag>
  );

  const Price = <Typography className={classes.price}>{formatPrice(props.selling_price_withtax)}</Typography>;

  const warrantyReassuranceContent = buildWarrantyContent();
  return (
    <div className={classes.cardContainer}>
      <ProductImage
        product={props}
        src={props.picture_new || props.picture || props.best_picture}
        width="100%"
        maxWidth={500}
        addBrightness={!props.picture_new}
        offerTag={isEligibleToLocalOffer && <OfferTag picto={LocalOfferPicto()} />}
        productStateTag={<ProductStateTag isStateNew={isProductNew(props)} />}
      />

      <div className={classes.container}>
        <Link
          href={{
            pathname: props.category1.length
              ? `/reconditionne/[category]/[category1]/[product]`
              : `/reconditionne/[category]/simple/[product]`,
            query: {
              category: props.product_type_slug,
              category1: props.category1.length ? props.category1[0] : null,
              product: props.slug,
            },
          }}
          passHref
        >
          <MuiLink onClick={() => props.onProductClick()} variant="body1" className={classes.link} color="textPrimary">
            {props.brand}
          </MuiLink>
        </Link>
        <Typography variant="body2">{getModelCategory(props)}</Typography>
        <Typography variant="body2">{props.model_reference}</Typography>
        <Box className={classes.reinsuranceTextContainer}>
          <Box className={classes.reinsuranceText}>
            <Box className={classes.reinsurancePicto}>{warrantyReassuranceContent.picto}</Box>
            {warrantyReassuranceContent.label}
          </Box>
        </Box>
        {isEligibleToLocalOffer && (
          <Box className={classes.reinsuranceTextContainer}>
            <Box className={classes.reinsuranceText}>
              <CheckCircle className={classes.reinsurancePicto} />
              Livraison en 48h ouvrées
            </Box>
          </Box>
        )}
        <Typography variant="caption" className={classNames(classes.priceInformations)}>
          {isProductNew(props) ? <div></div> : <div>À partir de</div>}
        </Typography>
        <div className={classes.priceContainerDesktop}>
          {Price}
          {!isProductNew(props) && MarketPriceTypography}
          {!isProductNew(props) && DiscountTag}
        </div>
        <div className={classes.priceContainerMobile}>
          {Price}
          <Box display={"flex"} justifyContent="space-between" alignItems="baseline">
            {!isProductNew(props) && MarketPriceTypography}
            {!isProductNew(props) && DiscountTag}
          </Box>
        </div>
      </div>
    </div>
  );
}

const useStyle = makeStyles((theme) => ({
  cardContainer: {
    position: "relative",
    margin: (props) => props.margin,
    width: (props) => props.width,
    minWidth: (props) => props.minWidth,

    [theme.breakpoints.down("md")]: {
      width: (props) => props.mdWidth,
    },
  },
  container: {
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.down("md")]: {
      marginLeft: theme.spacing(2),
    },
  },
  price: {
    fontWeight: 700,
    fontSize: 18,
  },
  priceInformations: {
    marginTop: theme.spacing(2),
    color: theme.palette.text.disabled,
  },
  priceContainerDesktop: {
    display: "flex",
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
    alignItems: "baseline",
    "& >*": {
      marginRight: theme.spacing(2),
    },
  },
  priceContainerMobile: {
    display: "grid",
    [theme.breakpoints.up("md")]: {
      display: "none",
    },
    alignItems: "baseline",
    "& >*": {
      marginRight: theme.spacing(2),
    },
  },
  marketPrice: {
    textDecoration: "line-through",
    color: theme.palette.text.disabled,
  },
  link: {
    fontWeight: 500,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(),
    color: theme.palette.text.primary,
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1,
      // Just in case `pointer-events: none` is set on a parent
      pointerEvents: "auto",
      content: "''",
      // IE10 bugfix, see https://stackoverflow.com/questions/16947967/ie10-hover-pseudo-class-doesnt-work-without-background-color
      backgroundColor: "rgba(0, 0, 0, 0)",
    },
  },
  reinsuranceTextContainer: {
    marginTop: theme.spacing(2),
  },
  reinsuranceText: {
    display: "flex",
    alignItems: "center",
    fontSize: 13,
    fontWeight: 400,
    color: theme.palette.secondary.main,
  },
  reinsurancePicto: {
    marginRight: 5,
    width: theme.spacing(3),
    display: "flex",
  },
}));

export default React.memo(ModelCard);
